import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { GitCompare, FileDown } from "lucide-react";
import { api } from "@/lib/api";
import { exportRivalDatasetComparePdf } from "@/lib/export";
import RffmSeasonsCompare from "@/components/RffmSeasonsCompare";
import { fmtPts } from "@/lib/datasetFmt";

const GOLD = "#D9B300";

const METRICS = [
  ["Año nac.", "anio_nacimiento", false],
  ["Posición", "posicion", false],
  ["Club rival", "rival_actual", false],
  ["Vs equipo EF", "equipo_ef", false],
  ["Convocatorias", "convocado_total", true],
  ["Partidos", "jugados_total", true],
  ["Goles", "goles_total", true],
  ["Minutos", "minutos_total_app", true],
  ["T. amarillas", "amarillas_total", true],
  ["Puntuación", "puntuacion", true],
];

export const RivalDatasetCompareDialog = ({ open, onOpenChange, players }) => {
  const [rffm, setRffm] = useState({});
  const [loadingRffm, setLoadingRffm] = useState(false);

  useEffect(() => {
    if (!open || !players?.length) return undefined;
    let alive = true;
    setLoadingRffm(true);
    api.get(`/rival-dataset/rffm-seasons?ids=${players.map((p) => p.jugador_id).join(",")}`)
      .then((r) => { if (alive) setRffm(r.data || {}); })
      .catch(() => { if (alive) setRffm({}); })
      .finally(() => { if (alive) setLoadingRffm(false); });
    return () => { alive = false; };
  }, [open, players]);

  if (!players?.length) return null;
  const best = {};
  METRICS.forEach(([, key, numeric]) => {
    if (!numeric) return;
    const vals = players.map((p) => (typeof p[key] === "number" ? p[key] : null)).filter((v) => v !== null);
    if (vals.length > 1) best[key] = Math.max(...vals);
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="rival-dataset-compare-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "Nunito" }}>
            <GitCompare className="h-5 w-5 text-purple-600" />
            Comparador de rivales
            <Button
              size="sm"
              className="ml-auto mr-6 bg-purple-600 hover:bg-purple-700 text-white font-bold"
              onClick={() => exportRivalDatasetComparePdf(players.map((p) => p.jugador_id))}
              data-testid="rival-dataset-compare-pdf"
            >
              <FileDown className="h-4 w-4 mr-1.5" /> PDF
            </Button>
          </DialogTitle>
        </DialogHeader>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th className="px-2 py-2"></th>
                {players.map((p) => (
                  <th key={p.jugador_id} className="px-2 py-2 text-center">
                    <div className="font-black text-purple-900" style={{ fontFamily: "Nunito" }}>{p.jugador}</div>
                    {p.ranking_general != null && (
                      <div className="text-[10px] text-slate-400 font-normal">Ranking #{p.ranking_general}</div>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {METRICS.map(([label, key, numeric]) => (
                <tr key={key}>
                  <td className="px-2 py-2 text-[10px] uppercase tracking-widest text-slate-500 whitespace-nowrap">{label}</td>
                  {players.map((p) => {
                    const v = p[key];
                    const isBest = numeric && best[key] !== undefined && v === best[key];
                    return (
                      <td key={p.jugador_id} className="px-2 py-2 text-center">
                        <span
                          className={`inline-block px-2 py-0.5 rounded font-bold ${isBest ? "text-slate-900" : "text-slate-700"}`}
                          style={isBest ? { backgroundColor: `${GOLD}33`, border: `1px solid ${GOLD}` } : {}}
                        >
                          {key === "puntuacion" && v != null ? fmtPts(v) : v ?? "—"}
                        </span>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <RffmSeasonsCompare
          loading={loadingRffm}
          cols={players.map((p) => ({ id: p.jugador_id, name: p.jugador, seasons: rffm[p.jugador_id]?.seasons || [] }))}
        />
      </DialogContent>
    </Dialog>
  );
};

export default RivalDatasetCompareDialog;
